import { type ReactNode, useCallback, useEffect, useState } from 'react';
import {
    FiActivity,
    FiBarChart2,
    FiBell,
    FiBook,
    FiChevronsLeft,
    FiChevronsRight,
    FiClipboard,
    FiCreditCard,
    FiPieChart,
    FiDollarSign,
    FiFileText,
    FiGrid,
    FiHelpCircle,
    FiList,
    FiLogOut,
    FiMenu,
    FiMinusCircle,
    FiPlusCircle,
    FiSettings,
    FiShield,
    FiTarget,
    FiTrendingUp,
    FiUsers,
    FiX
} from 'react-icons/fi';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts';
import { usePreferences } from '../../../contexts/PreferencesContext';
import logoClaro from '../../../assets/Logo_claro.png';
import logoEscuro from '../../../assets/Logo_escuro.png';
import type { PermissionModule } from '../../../types';
import styles from './Sidebar.module.css';

interface NavItem {
    path: string;
    labelKey: string;
    icon: ReactNode;
    separator?: boolean;
    // Gestão de Perfis de Acesso: módulo cujo VIEW controla se este item
    // aparece para o usuário. Itens sem isso (ex.: Configurações pessoais)
    // não passam pelo motor de permissões.
    permissionModule?: PermissionModule;
    // Ação a checar contra permissionModule — default 'VIEW'. Os atalhos de
    // "Lançar Receita/Despesa" checam CREATE porque são formulários de
    // criação, não telas de leitura.
    permissionAction?: 'VIEW' | 'CREATE' | 'EDIT' | 'DELETE' | 'EXPORT';
}

const NAV_ITEMS: NavItem[] = [
    { path: '/home', labelKey: 'nav.menu', icon: <FiGrid size={18} />, permissionModule: 'DASHBOARD' },
    { path: '/lancar-receita', labelKey: 'nav.lancarReceitas', icon: <FiPlusCircle size={18} />, permissionModule: 'LANCAMENTOS', permissionAction: 'CREATE' },
    { path: '/lancar-despesa', labelKey: 'nav.lancarDespesas', icon: <FiMinusCircle size={18} />, permissionModule: 'LANCAMENTOS', permissionAction: 'CREATE' },
    { path: '/lancamentos', labelKey: 'nav.historico', icon: <FiList size={18} />, permissionModule: 'LANCAMENTOS' },
    { path: '/cashflow', labelKey: 'nav.cashflow', icon: <FiTrendingUp size={18} />, permissionModule: 'FLUXO_CAIXA' },
    { path: '/graficos', labelKey: 'nav.graficos', icon: <FiBarChart2 size={18} />, permissionModule: 'DASHBOARD' },
    { path: '/reports', labelKey: 'nav.relatorios', icon: <FiFileText size={18} />, permissionModule: 'RELATORIOS' },
    { path: '/plano-contas', labelKey: 'nav.planoContas', icon: <FiBook size={18} />, permissionModule: 'PLANO_CONTAS' },
    { path: '/settings', labelKey: 'nav.configuracoes', icon: <FiSettings size={18} />, separator: true },
];

interface AdminLink {
    to: string;
    label: string;
    icon: ReactNode;
    /** So fica ativo na rota exata (ex.: /admin nao acende em /admin/clientes). */
    end?: boolean;
}

/** Menu do admin em subareas, como no prototipo do Analytics. */
const ADMIN_SECTIONS: Array<{ title: string | null; links: AdminLink[] }> = [
    { title: null, links: [{ to: '/admin', label: 'Admin', icon: <FiShield size={18} />, end: true }] },
    {
        title: 'Análise',
        links: [
            { to: '/admin/analytics', label: 'Analytics', icon: <FiBarChart2 size={18} />, end: true },
            { to: '/admin/analytics/assinaturas', label: 'Análise de assinaturas', icon: <FiPieChart size={18} /> },
            { to: '/admin/analytics/mapeamento', label: 'Mapeamento de clientes', icon: <FiTarget size={18} /> },
        ],
    },
    {
        title: 'Gestão',
        links: [
            { to: '/admin/clientes', label: 'Clientes', icon: <FiUsers size={18} /> },
            { to: '/admin/assinaturas', label: 'Assinaturas', icon: <FiCreditCard size={18} /> },
            { to: '/admin/planos', label: 'Planos', icon: <FiDollarSign size={18} /> },
        ],
    },
    {
        title: 'Sistema',
        links: [
            { to: '/admin/notificacoes', label: 'Notificações', icon: <FiBell size={18} /> },
            { to: '/admin/tracking', label: 'Tracking', icon: <FiActivity size={18} /> },
            { to: '/admin/pesquisa-onboarding', label: 'Pesquisa de Onboarding', icon: <FiClipboard size={18} /> },
            { to: '/admin/configuracoes', label: 'Configurações Admin', icon: <FiSettings size={18} /> },
        ],
    },
];

export function Sidebar() {
    const { user, logout, isAdmin, isGerente, isDemo, isDemoRoute, canAccess, hasPermission } = useAuth();
    const { t, prefs } = usePreferences();
    const darkMode = prefs.darkMode;
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem('sidebar-collapsed') === 'true'; }
        catch { return false; }
    });

    const toggleCollapsed = useCallback(() => {
        setCollapsed(prev => {
            const next = !prev;
            try { localStorage.setItem('sidebar-collapsed', String(next)); } catch { }
            document.documentElement.setAttribute('data-sidebar-collapsed', String(next));
            return next;
        });
    }, []);

    // Sync CSS attribute on mount
    useEffect(() => {
        document.documentElement.setAttribute('data-sidebar-collapsed', String(collapsed));
    }, [collapsed]);

    // Close sidebar on route change (mobile)
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    // Close sidebar on Escape key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMobileOpen(false);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    // Lock body scroll when mobile sidebar open
    useEffect(() => {
        if (mobileOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    const toggleMobile = useCallback(() => setMobileOpen(prev => !prev), []);

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const filteredNavItems = isAdmin
        ? []
        : isDemo
            ? NAV_ITEMS.filter((item) => isDemoRoute(item.path))
            : NAV_ITEMS
                .filter((item) => canAccess(item.path))
                .filter((item) => !item.permissionModule || hasPermission(item.permissionModule, item.permissionAction ?? 'VIEW'));

    return (
        <>
            <button
                className={styles.hamburger}
                onClick={toggleMobile}
                aria-label={t('nav.abrirMenu')}
            >
                <FiMenu size={22} />
            </button>

            {mobileOpen && (
                <div
                    className={styles.overlay}
                    onClick={() => setMobileOpen(false)}
                />
            )}

            <aside className={`${styles.sidebar} ${mobileOpen ? styles.sidebarOpen : ''} ${collapsed ? styles.collapsed : ''}`}>
                <div className={styles.sidebarTopRow}>
                    <div className={styles.logo}>
                        <img
                            src={darkMode ? logoClaro : logoEscuro}
                            alt="Financeiro Diário"
                            className={styles.logoImg}
                        />
                    </div>
                    <button
                        className={styles.collapseBtn}
                        onClick={toggleCollapsed}
                        aria-label={collapsed ? t('nav.expandirMenu') : t('nav.recolherMenu')}
                        title={collapsed ? t('nav.expandirMenu') : t('nav.recolherMenu')}
                    >
                        {collapsed ? <FiChevronsRight size={18} /> : <FiChevronsLeft size={18} />}
                    </button>
                    <button
                        className={styles.closeBtn}
                        onClick={() => setMobileOpen(false)}
                        aria-label={t('nav.fecharMenu')}
                    >
                        <FiX size={20} />
                    </button>
                </div>

                {/* <div className={styles.trialBadge}>
                <div className={styles.trialText}>Teste Gratuito</div>
                <div className={styles.trialProgress}>
                    <div className={styles.trialProgressBar} style={{ width: '50%' }} />
                </div>
                <div className={styles.trialDays}>15 dias</div>
            </div> */}

                <nav className={styles.nav}>
                    {filteredNavItems.map((item) => (
                        <div key={item.path}>
                            {item.separator && <div className={styles.navSeparator} />}
                            <NavLink
                                to={item.path}
                                className={({ isActive }) =>
                                    `${styles.navItem} ${isActive ? styles.active : ''}`
                                }
                                title={t(item.labelKey)}
                            >
                                <span className={styles.navIcon}>{item.icon}</span>
                                <span className={styles.navLabel}>{t(item.labelKey)}</span>
                            </NavLink>
                        </div>
                    ))}

                    {isAdmin &&
                        ADMIN_SECTIONS.map((section) => (
                            <div key={section.title ?? 'inicio'} className={styles.navSection}>
                                {section.title && <span className={styles.navSectionTitle}>{section.title}</span>}
                                {section.links.map((link) => (
                                    <NavLink
                                        key={link.to}
                                        to={link.to}
                                        end={link.end}
                                        className={({ isActive }) => `${styles.navItem} ${isActive ? styles.active : ''}`}
                                        title={link.label}
                                    >
                                        <span className={styles.navIcon}>{link.icon}</span>
                                        <span className={styles.navLabel}>{link.label}</span>
                                    </NavLink>
                                ))}
                            </div>
                        ))}

                    {/* TODO: Gestão de Usuários para GERENTE — em desenvolvimento
                    {isGerente && !isAdmin && (
                        <NavLink
                            to="/gestao-usuarios"
                            className={({ isActive }) =>
                                `${styles.navItem} ${styles.adminItem} ${isActive ? styles.active : ''}`
                            }
                            title={t('nav.gestaoUsuarios')}
                        >
                            <span className={styles.navIcon}><FiUsers size={18} /></span>
                            <span className={styles.navLabel}>{t('nav.gestaoUsuarios')}</span>
                        </NavLink>
                    )}
                    */}
                </nav>

                <div className={styles.footer}>
                    <div
                        className={styles.footerLink}
                        onClick={() => navigate('/help')}
                        style={{ cursor: 'pointer' }}
                    >
                        <FiHelpCircle size={16} />
                        <span>{t('nav.ajuda')}</span>
                    </div>
                </div>
                <div className={styles.userSection}>
                    <div className={styles.avatar}>
                        {user ? getInitials(user.name) : 'US'}
                    </div>
                    <div className={styles.userInfo}>
                        <div className={styles.userName}>{user?.name || 'Usuário'}</div>
                        <div className={styles.userEmail}>{user?.email || 'email@email.com'}</div>
                    </div>
                    <button className={styles.userMenu} onClick={logout} title="Sair">
                        <FiLogOut size={16} />
                    </button>
                </div>
            </aside>
        </>
    );
}